/**
 * useConfigProducao — leitura genérica do catálogo de Configurações da
 * Produção (GET /api/producao/config/catalogo) para hooks/telas que
 * precisam de UMA chave (checkpoints, política de pausa do congelamento,
 * mapa OSE→etapa…) sem re-implementar o fetch.
 *
 * Degradação honesta em camadas (mesma disciplina do useCheckpointsBipagem):
 *   - sem `ppcp:view`: nem tenta o request — `valorDe` devolve o fallback;
 *   - catálogo sem a chave (backend atrás do frontend): fallback;
 *   - request falhou: fallback.
 *
 * Cache module-level com TTL curto + dedupe de requests em voo: várias
 * telas/hooks montados juntos (Detalhe usa checkpoints E política de
 * pausa) geram UM GET, não N. `invalidarConfigProducao()` derruba o cache
 * — a tela Config chama após salvar para os consumidores relerem.
 */
import { useEffect, useMemo, useState } from "react";

import { useAuth } from "../../../context/AuthContext";
import { configCatalogo } from "../../../services/producaoV2";

const TTL_MS = 60_000;

let cache = { dados: null, em: 0, promessa: null };

/** Derruba o cache (chamar após salvar parâmetros na tela Config). */
export function invalidarConfigProducao() {
  cache = { dados: null, em: 0, promessa: null };
}

function buscarCatalogo(request) {
  if (cache.dados && Date.now() - cache.em < TTL_MS) {
    return Promise.resolve(cache.dados);
  }
  if (!cache.promessa) {
    cache.promessa = configCatalogo(request)
      .then((dados) => {
        cache = { dados, em: Date.now(), promessa: null };
        return dados;
      })
      .catch((err) => {
        cache.promessa = null;
        throw err;
      });
  }
  return cache.promessa;
}

export function useConfigProducao() {
  const { request, hasPermission } = useAuth();
  const podeLerConfig = hasPermission("ppcp:view");
  const [catalogo, setCatalogo] = useState(cache.dados);

  useEffect(() => {
    if (!podeLerConfig) return undefined;
    let ativo = true;
    buscarCatalogo(request)
      .then((dados) => {
        if (ativo) setCatalogo(dados);
      })
      .catch(() => {
        /* consumidores ficam no fallback */
      });
    return () => {
      ativo = false;
    };
  }, [request, podeLerConfig]);

  return useMemo(() => {
    const parametros = catalogo?.parametros || [];
    const porChave = Object.fromEntries(parametros.map((p) => [p.chave, p]));
    return {
      carregado: Boolean(catalogo),
      parametros,
      /** Valor efetivo da chave; fallback quando ausente/inacessível. */
      valorDe: (chave, fallback = null) =>
        chave in porChave && porChave[chave].valor !== undefined
          ? porChave[chave].valor
          : fallback,
    };
  }, [catalogo]);
}
